/**
 * ── ⭐ WHICH GPU ARE WE ON, AND DID WE ASK FOR THE RIGHT ONE? ───────────────────────────────────
 *
 * The owner, on a laptop with an RTX 3060 Mobile beside an Intel iGPU: *"We're using our intel
 * onboard graphics... we'll need the fix to work in an 'it just works' manner on any device."*
 *
 * ── THE FIX, AND ITS CEILING ────────────────────────────────────────────────────────────────────
 *
 * `navigator.gpu.requestAdapter({ powerPreference })` is the whole mechanism the web platform
 * offers, and we were passing NOTHING — so the browser took its default, which on every hybrid
 * laptop is the integrated GPU, to save battery. three r185 forwards the option straight through
 * (`WebGPUBackend.js:212`), so the fix itself is one field.
 *
 * ⚠️ **AND IT IS A HINT, NOT A COMMAND — THIS IS WHERE "just like a game" STOPS BEING TRUE, AND
 * NOBODY SHOULD RE-LITIGATE IT LATER.** A native game forces the discrete GPU by exporting
 * `NvOptimusEnablement = 0x00000001` (or AMD's `AmdPowerXpressRequestHighPerformance`) FROM ITS
 * OWN EXECUTABLE — a linker-level symbol the driver reads when the process starts. A web page has
 * no executable: the process the OS is choosing a GPU for is the BROWSER, and that choice is made
 * before a single line of our code runs. So on Windows the final authority is Windows Graphics
 * Settings (Settings → System → Display → Graphics → the browser → High performance) or the NVIDIA
 * Control Panel, and no amount of client code can override it.
 *
 * What we CAN do, and what this module is: ask correctly, then **find out what we actually got and
 * say so**. A silent 3× perf loss that nothing on screen explains is the failure here — the owner
 * measured a starved frame budget for a whole session before noticing the vendor string.
 *
 * ── ⛔ CHROME IGNORES `powerPreference` ENTIRELY, AND THAT IS DOCUMENTED, NOT INFERRED ──────────
 *
 * From Chrome's own WebGPU troubleshooting page (developer.chrome.com, read 2026-09):
 *
 *   > "Chrome does not support using multiple GPU adapters simultaneously."
 *   > "Chrome always uses the same GPU adapter that's been allocated for other Chrome workloads,
 *   >  which for laptops is generally the integrated graphics card, due to the power usage aspect."
 *   > "It means the `powerPreference` option doesn't have any impact when calling
 *   >  `requestAdapter()`."
 *
 * So the field below is CORRECT-BY-SPEC AND INERT IN CHROME. It is kept because it costs nothing,
 * because it is the only standard mechanism there is, and because Safari/macOS does honour it —
 * but nobody should spend an afternoon wondering why it did not move the frame rate on Windows.
 *
 * ⭐ **THE ONLY THING THAT MOVES A CHROME SESSION ONTO THE DISCRETE GPU IS MOVING THE WHOLE
 * BROWSER**: `chrome://flags/#force-high-performance-gpu` → Enabled → full restart, or the
 * equivalent Windows Graphics Settings / NVIDIA Control Panel per-app preference. Verified against
 * Chromium's own switch table (`gpu/config/gpu_switches.cc` defines `force-high-performance-gpu`
 * alongside `use-webgpu-adapter` and `use-webgpu-power-preference`).
 *
 * ── ⛔ NAMED LOSS: "DETECT THAT WE GOT THE WRONG GPU" IS UNDELIVERABLE FROM A PAGE ──────────────
 *
 * The first cut of this module probed BOTH power preferences and compared the adapters, so that a
 * warning could fire only on a machine that provably has two GPUs. Two things killed it, and both
 * are worth keeping written down:
 *
 *   1. **It could never have fired.** `hybrid` was defined as "the two adapters DIFFER" and the
 *      warning then required them to be the SAME — a contradiction, so the branch was dead on
 *      arrival. Its tests asserted only `toBeNull()`, so they PINNED the broken behaviour instead
 *      of catching it. That is this repo's signature failure, authored here, and it is recorded
 *      rather than quietly patched.
 *   2. **Even written correctly it cannot work in Chrome.** Chrome exposes ONE adapter, so both
 *      preferences return the same device on every machine — a page cannot distinguish "this
 *      laptop has one GPU" from "this laptop has two and the browser pinned us to the slow one".
 *      The information is not available at this altitude, and no amount of care makes it so.
 *
 * What replaced it is what actually worked: **name the adapter, always, and put the remedy where a
 * curious person finds it.** The owner diagnosed his own RTX 3060 sitting at 0 % by reading
 * `intel gen-12lp` off the perf bar — the report is the whole feature.
 */

/** What `requestAdapter` accepts. `undefined` = "browser's choice", which is what we shipped. */
export type GpuPowerPreference = 'high-performance' | 'low-power';

/**
 * ⭐ THE DEFAULT IS `high-performance`, AND IT IS A PRODUCT DECISION WRITTEN DOWN HERE.
 *
 * We are a 3D game, not a text page: on a machine with a discrete GPU the discrete GPU is the one
 * the player wants, and the measured alternative was a session pinned at the dynamic-resolution
 * floor and still over budget. The battery cost is real and is accepted — a game that runs at 20 fps
 * to save power has not saved the player anything.
 *
 * `?gpu=low-power` is the named control arm, exactly as `?csm=0` and `--dilate 0` are theirs: the
 * A/B "how much of this frame is the iGPU" is a legitimate measurement and must stay reachable.
 */
export const DEFAULT_POWER_PREFERENCE: GpuPowerPreference = 'high-performance';

/**
 * Lenient, never throws — a stale bookmark or a typo lands on the default with a warning rather
 * than booting a black page. `default` is a real value here: it means "pass nothing", which is the
 * pre-fix behaviour and the only way to reproduce it.
 */
export function parsePowerPreference(
  raw: string | null | undefined,
): { preference: GpuPowerPreference | undefined; known: boolean } {
  if (raw === null || raw === undefined || raw === '') {
    return { preference: DEFAULT_POWER_PREFERENCE, known: true };
  }
  const v = raw.toLowerCase();
  if (v === 'high-performance' || v === 'high' || v === 'discrete') {
    return { preference: 'high-performance', known: true };
  }
  if (v === 'low-power' || v === 'low' || v === 'integrated') return { preference: 'low-power', known: true };
  // "Pass no powerPreference at all" — the pre-fix arm, and the only honest way to spell it.
  if (v === 'default' || v === 'browser') return { preference: undefined, known: true };
  return { preference: DEFAULT_POWER_PREFERENCE, known: false };
}

/** The four fields `GPUAdapterInfo` carries. All may be empty strings — Chrome redacts some. */
export interface GpuAdapterFacts {
  vendor: string;
  architecture: string;
  device: string;
  description: string;
}

export interface GpuProbe {
  /** `false` ⇒ `navigator.gpu` is absent entirely (the WebGL2-fallback cause). */
  webgpu: boolean;
  /** What we asked for and what came back. `null` when no adapter was returned. */
  requested: GpuPowerPreference | undefined;
  adapter: GpuAdapterFacts | null;
}

const facts = (info: unknown): GpuAdapterFacts | null => {
  if (typeof info !== 'object' || info === null) return null;
  const i = info as Partial<Record<keyof GpuAdapterFacts, unknown>>;
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    vendor: str(i.vendor),
    architecture: str(i.architecture),
    device: str(i.device),
    description: str(i.description),
  };
};

/**
 * Ask for an adapter and read its info, WITHOUT creating a device.
 *
 * ⚠️ An adapter is cheap and a device is not — `requestDevice` is what allocates. This never calls
 * one, which is what makes the probe affordable at boot and what stops it disturbing the renderer's
 * own adapter/device pair.
 */
async function askAdapter(
  gpu: { requestAdapter(o?: { powerPreference?: GpuPowerPreference }): Promise<unknown> },
  powerPreference: GpuPowerPreference | undefined,
): Promise<GpuAdapterFacts | null> {
  try {
    const a = await gpu.requestAdapter(powerPreference === undefined ? {} : { powerPreference });
    if (a === null || typeof a !== 'object') return null;
    return facts((a as { info?: unknown }).info);
  } catch {
    return null;
  }
}

/**
 * The boot probe. Runs BEFORE the renderer is constructed so the report is available even when
 * three then falls back to WebGL2 — which is the case that reads `GI · CAN'T TELL` and had no
 * diagnosis at all until this existed.
 */
export async function probeGpus(
  nav: { gpu?: { requestAdapter(o?: { powerPreference?: GpuPowerPreference }): Promise<unknown> } },
  requested: GpuPowerPreference | undefined,
): Promise<GpuProbe> {
  const gpu = nav.gpu;
  if (gpu === undefined) return { webgpu: false, requested, adapter: null };
  return { webgpu: true, requested, adapter: await askAdapter(gpu, requested) };
}

/** `NVIDIA GeForce RTX 3060 Laptop GPU` — the most specific non-empty field, for a one-line report. */
export function adapterName(a: GpuAdapterFacts | null): string {
  if (a === null) return 'unknown GPU';
  const parts = [a.description, a.device, [a.vendor, a.architecture].filter(Boolean).join(' ')];
  return parts.find((p) => p.trim() !== '') ?? 'unnamed adapter';
}

/**
 * ⭐ THE REMEDY, AND IT IS A TOOLTIP RATHER THAN AN ALARM.
 *
 * A page cannot tell whether this machine even HAS a second GPU (see the NAMED LOSS above), so a
 * warning would either cry wolf on every single-GPU office laptop and school tablet or say nothing
 * at all. The honest shape is a sentence that is ALWAYS available beside the adapter name and never
 * shouts: the person reading `intel gen-12lp` on a machine they know has a 3060 is the one who
 * needs it, and they are already looking at exactly the right place.
 */
export const GPU_REMEDY =
  'Chrome ignores the page’s powerPreference: it renders every tab on one adapter, and on a laptop ' +
  'that is normally the integrated GPU. To move it, move the whole browser — ' +
  'chrome://flags/#force-high-performance-gpu → Enabled → restart Chrome fully, or Windows ' +
  'Settings → System → Display → Graphics → the browser → Options → High performance. A page cannot ' +
  'do it: a native game forces the choice with a linker symbol in its own .exe, and the process the ' +
  'OS is choosing for here is the browser.';

/** `WebGPU · NVIDIA GeForce RTX 3060 Laptop GPU (high-performance)` — the perf bar's GPU line. */
export function describeGpu(p: GpuProbe): string {
  if (!p.webgpu) return 'no WebGPU — navigator.gpu is absent';
  if (p.adapter === null) return 'WebGPU present, no adapter returned';
  const asked = p.requested ?? 'browser default';
  return `${adapterName(p.adapter)} (${asked})`;
}
